import { createTheme, alpha } from '@mui/material/styles';

const INDIGO = {
  50:  '#EEF2FF',
  100: '#E0E7FF',
  200: '#C7D2FE',
  400: '#818CF8',
  500: '#6366F1',
  600: '#4F46E5',
  700: '#4338CA',
  800: '#3730A3',
  900: '#312E81',
};

const VIOLET = {
  500: '#8B5CF6',
  600: '#7C3AED',
  700: '#6D28D9',
};

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main:          INDIGO[600],
      light:         INDIGO[400],
      dark:          INDIGO[800],
      contrastText:  '#fff',
    },
    secondary: {
      main:          VIOLET[600],
      light:         VIOLET[500],
      dark:          VIOLET[700],
      contrastText:  '#fff',
    },
    background: {
      default: '#F9FAFB',
      paper:   '#FFFFFF',
    },
    text: {
      primary:   '#111827',
      secondary: '#6B7280',
      disabled:  '#9CA3AF',
    },
    divider: '#E5E7EB',
    success: { main: '#059669', light: '#D1FAE5', contrastText: '#fff' },
    warning: { main: '#D97706', light: '#FEF3C7', contrastText: '#fff' },
    error:   { main: '#DC2626', light: '#FEE2E2', contrastText: '#fff' },
    info:    { main: '#0284C7', light: '#E0F2FE', contrastText: '#fff' },
  },

  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.08 },
    h2: { fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1 },
    h3: { fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 },
    h4: { fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1.3 },
    h5: { fontWeight: 600, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600, letterSpacing: '-0.005em' },
    subtitle1: { fontWeight: 500, lineHeight: 1.5 },
    subtitle2: { fontWeight: 500, lineHeight: 1.5 },
    body1: { lineHeight: 1.75 },
    body2: { lineHeight: 1.65 },
    button: { fontWeight: 600, letterSpacing: '0' },
    caption: { lineHeight: 1.5 },
    overline: { fontWeight: 600, letterSpacing: '0.08em' },
  },

  shape: { borderRadius: 10 },

  shadows: [
    'none',
    '0 1px 2px 0 rgba(0,0,0,0.05)',
    '0 1px 3px 0 rgba(0,0,0,0.08), 0 1px 2px -1px rgba(0,0,0,0.08)',
    '0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -2px rgba(0,0,0,0.08)',
    '0 6px 10px -2px rgba(0,0,0,0.08), 0 2px 6px -2px rgba(0,0,0,0.06)',
    '0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -4px rgba(0,0,0,0.05)',
    '0 12px 20px -4px rgba(0,0,0,0.08), 0 4px 8px -4px rgba(0,0,0,0.05)',
    '0 16px 25px -5px rgba(0,0,0,0.08), 0 6px 10px -5px rgba(0,0,0,0.04)',
    '0 20px 30px -6px rgba(0,0,0,0.08), 0 8px 12px -6px rgba(0,0,0,0.04)',
    '0 24px 35px -7px rgba(0,0,0,0.08), 0 10px 14px -7px rgba(0,0,0,0.04)',
    '0 28px 40px -8px rgba(0,0,0,0.08), 0 12px 16px -8px rgba(0,0,0,0.04)',
    '0 32px 45px -9px rgba(0,0,0,0.08), 0 14px 18px -9px rgba(0,0,0,0.04)',
    '0 36px 50px -10px rgba(0,0,0,0.08), 0 16px 20px -10px rgba(0,0,0,0.04)',
    '0 40px 55px -11px rgba(0,0,0,0.08), 0 18px 22px -11px rgba(0,0,0,0.04)',
    '0 44px 60px -12px rgba(0,0,0,0.10), 0 20px 24px -12px rgba(0,0,0,0.05)',
    '0 48px 65px -13px rgba(0,0,0,0.10), 0 22px 26px -13px rgba(0,0,0,0.05)',
    '0 52px 70px -14px rgba(0,0,0,0.10), 0 24px 28px -14px rgba(0,0,0,0.05)',
    '0 56px 75px -15px rgba(0,0,0,0.10), 0 26px 30px -15px rgba(0,0,0,0.05)',
    '0 60px 80px -16px rgba(0,0,0,0.12), 0 28px 32px -16px rgba(0,0,0,0.06)',
    '0 64px 85px -17px rgba(0,0,0,0.12), 0 30px 34px -17px rgba(0,0,0,0.06)',
    '0 68px 90px -18px rgba(0,0,0,0.12), 0 32px 36px -18px rgba(0,0,0,0.06)',
    '0 72px 95px -19px rgba(0,0,0,0.12), 0 34px 38px -19px rgba(0,0,0,0.06)',
    '0 76px 100px -20px rgba(0,0,0,0.12), 0 36px 40px -20px rgba(0,0,0,0.06)',
    '0 80px 105px -21px rgba(0,0,0,0.14), 0 38px 42px -21px rgba(0,0,0,0.07)',
    '0 84px 110px -22px rgba(0,0,0,0.14), 0 40px 44px -22px rgba(0,0,0,0.07)',
  ],

  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          transition: 'all 0.15s ease',
        },
        sizeLarge: {
          padding: '10px 24px',
          fontSize: '0.9375rem',
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${INDIGO[600]} 0%, ${INDIGO[700]} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${INDIGO[700]} 0%, ${INDIGO[800]} 100%)`,
          },
        },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 14,
          border: '1px solid #E5E7EB',
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500, borderRadius: 6 },
        sizeSmall: { height: 22, fontSize: '0.72rem' },
      },
    },

    MuiTextField: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: INDIGO[400] },
          },
        },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderBottom: '1px solid #E5E7EB',
          backgroundColor: alpha('#FFFFFF', 0.85),
          backdropFilter: 'blur(12px)',
          color: '#111827',
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: { borderRight: '1px solid #E5E7EB', backgroundColor: '#FFFFFF' },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: 8, transition: 'background 0.15s' },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          '&.Mui-selected': { fontWeight: 600 },
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10 },
      },
    },

    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 14 },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: { borderColor: '#E5E7EB' },
      },
    },
  },
});
